import { useState } from 'react';
import { Play, Loader, AlertCircle, CheckCircle, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import type { QueryFormData } from '../../../types/queryForm';
import type { QueryExecutionResult, AutomationInterval } from '../../../types/query';
import { AUTOMATION_INTERVALS } from '../../../types/query';
import { queryService } from '../../../services/queryService';
import QueryResultViewer from '../QueryResultViewer';

interface Step1DefinitionsProps {
  formData: QueryFormData;
  onChange: (data: Partial<QueryFormData>) => void;
  onColumnsDiscovered: (columns: string[]) => void;
  isEditing: boolean;
}

export default function Step1Definitions({
  formData,
  onChange,
  onColumnsDiscovered,
  isEditing
}: Step1DefinitionsProps) {
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<QueryExecutionResult | null>(null);
  const [showAutomation, setShowAutomation] = useState(formData.automationEnabled);

  const handleNameChange = (value: string) => {
    onChange({ name: value });
    if (!isEditing) {
      const slug = queryService.slugify(value);
      onChange({ name: value, endpointSlug: slug });
    }
  };

  const handleExecuteTest = async () => {
    if (!formData.sqlQuery.trim()) return;

    setIsExecuting(true);
    setExecutionResult(null);

    try {
      const result = await queryService.executeRawQuery(formData.sqlQuery);
      setExecutionResult(result);

      if (result.success && result.columns.length > 0) {
        onColumnsDiscovered(result.columns);
      }
    } catch (error:any) {
      setExecutionResult({
        success: false,
        data: [],
        columns: [],
        rowCount: 0,
        executionTime: 0,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Definicoes do Serviço</h3>
        <p className="text-sm text-gray-500">Configure o nome, descricao e a consulta SQL que sera executada.</p>
      </div>

      <div className="grid grid-cols-1 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Serviço *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ex: Listar usuarios ativos"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descricao
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => onChange({ description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={2}
            placeholder="Descricao opcional do serviço"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Query SQL *
            </label>
            <button
              type="button"
              onClick={handleExecuteTest}
              disabled={isExecuting || !formData.sqlQuery.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
            >
              {isExecuting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Testar Consulta
            </button>
          </div>
          <textarea
            value={formData.sqlQuery}
            onChange={(e) => onChange({ sqlQuery: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            rows={8}
            placeholder="SELECT * FROM users WHERE active = true"
          />
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAutomation(!showAutomation)}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Zap className={`w-4 h-4 ${formData.automationEnabled ? 'text-blue-600' : 'text-gray-400'}`} />
            <span className="text-sm font-medium text-gray-700">Periodicidade de Atualizacao</span>
            {formData.automationEnabled && (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                Ativo
              </span>
            )}
          </div>
          {showAutomation ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {showAutomation && (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="automationEnabled"
                checked={formData.automationEnabled}
                onChange={(e) => onChange({ automationEnabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="automationEnabled" className="text-sm text-gray-700">
                Habilitar atualizacao automatica
              </label>
            </div>

            {formData.automationEnabled && (
              <div className="pl-7 space-y-4 border-l-2 border-blue-200 ml-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL do Webhook *
                  </label>
                  <input
                    type="url"
                    value={formData.automationWebhookUrl}
                    onChange={(e) => onChange({ automationWebhookUrl: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://exemplo.com/webhook"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    O resultado da consulta sera enviado via POST para esta URL
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequencia de Execucao
                  </label>
                  <select
                    value={formData.automationInterval}
                    onChange={(e) => onChange({ automationInterval: e.target.value as AutomationInterval })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    {AUTOMATION_INTERVALS.map((interval) => (
                      <option key={interval.value} value={interval.value}>
                        {interval.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {executionResult && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            {executionResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <span className={`font-medium ${
              executionResult.success ? 'text-green-700' : 'text-red-700'
            }`}>
              {executionResult.success
                ? `${executionResult.rowCount} resultado(s) em ${executionResult.executionTime}ms`
                : 'Erro na execução'
              }
            </span>
          </div>
          <QueryResultViewer result={executionResult} showExport={false} />
        </div>
      )}
    </div>
  );
}
